// Los dos chips de variación del mockup (`.chip-delta`). Son DOS y no uno a propósito: el volumen
// varía en porcentaje RELATIVO y la captura en PUNTOS PORCENTUALES, y confundirlos cambia el número.
import { cn } from '@/lib/cn';
import { changeState, formatChangePct, formatPp } from '../lib/originationMetrics';

const CHIP =
    'inline-flex rounded-[3px] px-1.5 py-px text-[10px] font-bold tabular-nums leading-[1.35]';
const UP = 'bg-success-soft text-success';
const DOWN = 'bg-destructive/15 text-destructive';
const FLAT = 'bg-muted text-muted-foreground';

/**
 * Δ del volumen: porcentaje RELATIVO contra la ventana anterior.
 *
 * 🔴 El valor YA viene en escala de porcentaje desde el silo (`12` = 12 %): acá NO se re-escala. Por
 * eso el umbral de «plano» es ±2 puntos y no ±0.02 — es el mismo corte del mock, en la otra escala.
 */
export function VolumeDeltaChip({
    changePct,
    current,
    previous,
}: {
    changePct: number | null;
    current: number;
    previous: number;
}) {
    const state = changeState(changePct, current, previous);
    if (state.kind === 'fromZero') {
        return (
            <span
                className={cn(CHIP, UP)}
                title="La ventana anterior no tuvo colocación: hay crecimiento, pero ningún porcentaje que calcular"
            >
                ↑ desde 0
            </span>
        );
    }
    if (state.kind !== 'pct') {
        return (
            <span
                className="text-[10px] text-muted-foreground"
                title={
                    state.kind === 'noActivity'
                        ? 'Sin colocación en esta ventana ni en la anterior'
                        : 'Sin ventana anterior con qué comparar'
                }
            >
                —
            </span>
        );
    }
    const tone = state.pct > 2 ? UP : state.pct < -2 ? DOWN : FLAT;
    return <span className={cn(CHIP, tone)}>{formatChangePct(state.pct)}</span>;
}

/**
 * Δ de una tasa: PUNTOS PORCENTUALES, nunca un porcentaje relativo.
 *
 * `null` es guion con su explicación: sin ventas en alguna de las dos ventanas no hay tasa que
 * restar, y un `0.0 pp` afirmaría que la captura se mantuvo igual.
 */
export function CaptureDeltaChip({ changePp }: { changePp: number | null }) {
    if (changePp === null) {
        return (
            <span
                className="text-[10px] text-muted-foreground"
                title="Alguna de las dos ventanas no tuvo ventas: no hay captura que comparar"
            >
                —
            </span>
        );
    }
    const tone = changePp > 0.5 ? UP : changePp < -0.5 ? DOWN : FLAT;
    return <span className={cn(CHIP, tone)}>{formatPp(changePp)}</span>;
}

/**
 * Δ de la cosecha, en PUNTOS PORCENTUALES.
 *
 * Es su propio chip y no el de la captura porque el `null` significa otra cosa: acá es «la ventana
 * anterior no tenía créditos con la antigüedad suficiente», no «no hubo ventas».
 */
export function CohortDeltaChip({
    changePp,
    previousEligibleCredits,
}: {
    changePp: number | null;
    previousEligibleCredits: number;
}) {
    if (changePp === null) {
        return (
            <span
                className="text-[10px] text-muted-foreground"
                title={
                    previousEligibleCredits === 0
                        ? 'La ventana anterior no tuvo créditos con la antigüedad suficiente: no hay cosecha que comparar'
                        : 'Esta ventana no tiene créditos elegibles: no hay cosecha que comparar'
                }
            >
                —
            </span>
        );
    }
    const tone = changePp > 0.5 ? UP : changePp < -0.5 ? DOWN : FLAT;
    return <span className={cn(CHIP, tone)}>{formatPp(changePp)}</span>;
}

/**
 * Δ del enganche contra el mínimo que el plan exigía, en PUNTOS PORCENTUALES.
 *
 * 🔴 El color va al revés que en los otros chips: quedarse pegado al mínimo es el caso RIESGOSO, no
 * el bueno. Son los mismos cortes del mock (+10 pp holgado · menos de +3 pp pegado al mínimo).
 */
export function DownPaymentDeltaChip({ overMinPp }: { overMinPp: number | null }) {
    if (overMinPp === null) return null;
    const tone = overMinPp > 10 ? UP : overMinPp < 3 ? DOWN : FLAT;
    return (
        <span
            className={cn(CHIP, tone, 'ml-1 text-[9px]')}
            title={
                overMinPp < 3
                    ? 'Cobró apenas por encima del mínimo que le exigía su plan'
                    : 'Cuánto cobró por encima del mínimo que le exigía su plan'
            }
        >
            {formatPp(overMinPp)}
        </span>
    );
}
